import { ClientSearchFilters } from "@/modules/clients/types";
import { BaseResponse } from "@/modules/common/dtos";
import {
  BasePaginationResponse,
  LookupsParams,
  FormFieldsResponse,
  User,
  Status,
} from "@/modules/common/dtos/index.v2";
import { JourneysFilters } from "@/modules/leads/types";
import { ListingDashboardFilters } from "@/modules/listing-dashboard/types";
import { NextActionsFilters } from "@/modules/next-actions/types";
import { SalesFilters } from "@/modules/sales/types";
import { Rank } from "@/modules/user/types/index.v2";
import { ListItemButtonProps } from "@mui/material";
import { UUID } from "crypto";
import { ReactNode } from "react";
import { ToastContent, ToastOptions } from "react-toastify";

export enum EnvironmentVariables {
  Environment = import.meta.env.REACT_APP_ENVIRONMENT,
  MIXPANEL_TOKEN = import.meta.env.REACT_APP_MIXPANEL_TOKEN,
  PUBLIC_DOMAIN = import.meta.env.REACT_APP_PUBLIC_DOMAIN,
  CRM_API_KEY = import.meta.env.REACT_APP_CRM_API_KEY,
  CRM_API_KEY_AGENT = import.meta.env.REACT_APP_CRM_API_KEY_AGENT,
  CRM_BASE_URL = import.meta.env.REACT_APP_CRM_BASE_API_URL,
  CRM_NAVIGATE_URL = import.meta.env.REACT_APP_CRM_NAVIGATE_URL,
  OLD_CRM = import.meta.env.REACT_APP_OLD_CRM,
  CRM_PUBLIC_DOMAIN = import.meta.env.REACT_APP_CRM_PUBLIC_DOMAIN,
  AWS_CLIENT_ID = import.meta.env.REACT_APP_AWS_CLIENT_ID,
  AWS_POOL_ID = import.meta.env.REACT_APP_AWS_POOL_ID,
  COGNITO_REDIRECT_URI = import.meta.env.REACT_APP_COGNITO_REDIRECT_URI,
  COGNITO_DOMAIN = import.meta.env.REACT_APP_COGNITO_DOMAIN,
  S3BUCKET_URL = import.meta.env.REACT_APP_S3BUCKET_URL,
  COMPANY_NAME = import.meta.env.REACT_APP_COMPANY_NAME,
  IS_FRANCHISE = import.meta.env.REACT_APP_IS_FRANCHISE,
  EREALTY_URL = import.meta.env.REACT_APP_EREALTY_URL,
  EREALTY_API_URL = import.meta.env.REACT_APP_EREALTY_API_URL,
  TASKEEN_URL = import.meta.env.REACT_APP_TASKEEN_URL,
  PARTNERS_URL = import.meta.env.REACT_APP_PARTNERS_URL,
  NAWY_URL = import.meta.env.REACT_APP_NAWY_URL,
  COOKIES_SHARED_DOMAIN = import.meta.env.REACT_APP_COOKIES_SHARED_DOMAIN,
  LISTING_API_URL = import.meta.env.REACT_APP_LISTING_API_URL,
  NEW_LISTING_API_URL = import.meta.env.REACT_APP_NEW_LISTING_API_URL,
  LISTING_URL = import.meta.env.REACT_APP_LISTING_URL,
  NAWY_OWNERS_PORTAL_URL = import.meta.env.REACT_APP_NAWY_OWNERS_PORTAL_URL,
  STATUS_GLOSSARY_URL = import.meta.env.REACT_APP_STATUS_GLOSSARY_URL,
  MIDAS_API = import.meta.env.REACT_APP_MIDAS_API,
  AUTO_ASSIGNMENT_URL = import.meta.env.REACT_APP_AUTO_ASSIGNMENT_URL,
  SALES_PORTAL_URL = import.meta.env.REACT_APP_NAWY_SALES_PORTAL_URL,
  NEW_CRM_BASE_API_URL = import.meta.env.REACT_APP_NEW_CRM_BASE_API_URL,
  SSE_GATEWAY_URL = import.meta.env.REACT_APP_SSE_GATEWAY_URL,
  NEW_DYNAMIC_CRM_BASE_API_URL = import.meta.env
    .REACT_APP_NEW_DYNAMIC_CRM_BASE_API_URL,
  UMS_API_URL = import.meta.env.REACT_APP_UMS_API_URL,
  AUDIT_SERVICE_URL = import.meta.env.REACT_APP_AUDIT_SERVICE_URL,
  UMS_API_URL_V2 = import.meta.env.REACT_APP_UMS_API_URL_V2,
  MOENGAGE_APP_ID = import.meta.env.REACT_APP_MOENGAGE_APP_ID,
  MOENGAGE_DC = import.meta.env.REACT_APP_MOENGAGE_DC,
  GOOGLE_ANALYTICS_ID = import.meta.env.REACT_APP_GOOGLE_ANALYTICS_ID,
  FAUNA_API_URL = import.meta.env.REACT_APP_FAUNA_API_URL,
  FAUNA_API_KEY = import.meta.env.REACT_APP_FAUNA_API_KEY,
  REACT_APP_YMIR_URL = import.meta.env.REACT_APP_YMIR_URL,
  UMS_PUBLIC_DOMAIN = import.meta.env.REACT_APP_UMS_PUBLIC_DOMAIN,
  PROPERTY_MANAGEMENT_API_URL = import.meta.env
    .REACT_APP_PROPERTY_MANAGEMENT_API_URL,
  HOSPITALITY_API_URL = import.meta.env.REACT_APP_HOSPITALITY_API_URL,
  LISTING_RECOMMENDATIONS_API_KEY = import.meta.env
    .LISTING_RECOMMENDATIONS_API_KEY,
  TASKS_API_URL = import.meta.env.REACT_APP_TASKS_API_URL,
  CONTRACTS_API_URL = import.meta.env.REACT_APP_CONTRACTS_API_URL,
  CONTRACTS_FRONTEND_URL = import.meta.env.REACT_APP_CONTRACTS_FRONTEND_URL,
  CONSTRUCTION_API_URL = import.meta.env
    .REACT_APP_UNLOCKED_CONSTRUCTION_API_URL,
  ZOOMKEEPER_API_URL = import.meta.env.REACT_APP_ZOOMKEEPER_API_URL,
  WORKFLOW_MANAGER_BASE_API_URL = import.meta.env
    .REACT_APP_WORKFLOW_MANAGER_BASE_API_URL,
}

export type IconType = {
  fill?: string;
  width?: string | number;
  height?: string | number;
  stroke?: string;
};

export interface LoggerDataType {
  method?: string;
  data?: any;
  message?: string;
  serviceName?: string;
}

export interface CognitoIdToken {
  at_hash: string;
  aud: string;
  auth_time: number;
  "cognito:groups": string[];
  "cognito:username": string;
  crmId: string;
  email: string;
  email_verified: boolean;
  exp: number;
  iat: number;
  identities: Array<any>; // You might want to define a more specific type for identities
  isSupervisor: "true" | "false";
  iss: string;
  jti: string;
  name: string;
  origin_jti: string;
  phone_number: string;
  phone_number_verified: boolean;
  sub: string;
  token_use: string;
}

export interface OptionProps {
  value: string;
  label: string;
}

export interface StatusFormField {
  id: number;
  title: string;
  field_type: string;
  backend_field_id: number;
}

export interface Rule {
  label: string;
  value: {
    isMulti?: boolean;
    required?: boolean;
    [key: string]: any;
  };
}

export interface StatusFormsField {
  id: number;
  rules: Rule[];
  status_form_id: number;
  status_form_field_id: number;
  status_form_field: StatusFormField;
}

export interface FormFields {
  id: number;
  title: string;
  status_forms_fields: StatusFormsField[];
}

export interface DeveloperTrackItem {
  id: string;
  leadUId: string;
  developerUId: string;
  developerUserUId: string;
  developerName?: string;
  developerLogoPath?: string;
  agentName?: string;
  formData?: Record<string, unknown>;
}

export interface FormFactoryReadyApi {
  getValidatedValues: () => Promise<Record<string, any> | null>;
}

export interface FormFactoryProps {
  submitFun: (values: any, formType?: "sale" | "form") => void;
  formFields: FormFieldsResponse;
  status?: {
    editLeadStatusModalOpen: boolean;
    setEditLeadStatusModalOpen: (value: boolean) => void;
    handleChangeStatus: (event: any) => void;
    handleChangeStatusComment: (event: any) => void;
    statusChangeComment: string;
    data: any;
    state: number | null;
  };
  selectedStatus?: Status;
  isLoadingSubmition: boolean;
  handleClose: () => void;
  showComment: boolean;
 
  hideFooter?: boolean;
  // Called once the internal react-hook-form instance is ready, handing the
  // host an imperative handle to validate + read the form's values on demand.
  onReady?: (api: FormFactoryReadyApi) => void;
 
  onSaveAndCreateTracks?: (values: Record<string, any>) => void;
}

export interface BackendFieldName {
  id: number;
  backend_field_name: string;
}

export interface FormFactoryData {
  id: number;
  title: string;
  status_forms_fields: StatusFormsField[];
}

export interface FormFieldByTypeProps {
  fieldType: string;
  fullName: string;
  control: any;
  fieldRules: Record<string, any>;
  commonProps: {
    fullWidth: boolean;
    margin: "normal";
  };
  status_form_field: {
    title: string;
    backend_field_id: number;
  };
  isMulti: boolean;
  options?: OptionProps[];
  compoundsData?: any[];
  developerOptions?: OptionProps[];
  propertyTypes?: OptionProps[];
  areas?: any[];
  compoundOptions?: Record<string, OptionProps[]>;
  setAreaOption?: React.Dispatch<
    React.SetStateAction<Record<string, OptionProps>>
  >;
  watch?: any;
  setValue?: any;
}

export interface Compound {
  area_id: number;
  developer_id: number;
  has_offers: boolean;
  id: number;
  image_path: string;
  name: string;
  nawy_organization_id: number;
  slug: string;
  updated_at: string;
}

export interface FormFactoryDateRules {
  comparison_type?: { label: string; value: string };
  offset_type?: { label: string; value: string };
  offset_value?: number;
  minDate: Date;
  maxDate: Date;
  defaultValue?: string;
}

export interface GetPersonalMeetingRoomResponse {
  zoom_meeting_history: {
    lead_id: number;
    user_id: string;
    meeting_timing: string;
  };
}

export enum ToastType {
  SUCCESS = "success",
  ERROR = "error",
  INFO = "info",
  WARNING = "warning",
}

export interface ToastParams {
  type: ToastType;
  message: ToastContent;
  toastId: string;
  options?: ToastOptions;
}

export interface StatusesDataFetchingHookProps {
  isMoveLead?: boolean;
  leadTypeUId?: UUID;
  organizationUId?: string;
}

export const TOAST_IDS = {
  ADD_AUTO_ASSIGNMENT_POOL: "addAutoAssignmentPool",
  ADD_AUTO_ASSIGNMENT_POOL_ERROR: "addAutoAssignmentPoolError",
  EDIT_AUTO_ASSIGNMENT_POOL: "editAutoAssignmentPool",
  EDIT_AUTO_ASSIGNMENT_POOL_ERROR: "editAutoAssignmentPoolError",
  DELETE_AUTO_ASSIGNMENT_POOL: "deleteAutoAssignmentPool",
  DELETE_AUTO_ASSIGNMENT_POOL_ERROR: "deleteAutoAssignmentPoolError",
  ADD_LOCATION_TO_AUTO_ASSIGNMENT_POOL: "addLocationToAutoAssignmentPool",
  ADD_LOCATION_TO_AUTO_ASSIGNMENT_POOL_ERROR:
    "addLocationToAutoAssignmentPoolError",
  DELETE_LOCATION_FROM_AUTO_ASSIGNMENT_POOL:
    "deleteLocationFromAutoAssignmentPool",
  DELETE_LOCATION_FROM_AUTO_ASSIGNMENT_POOL_ERROR:
    "deleteLocationFromAutoAssignmentPoolError",
  UPDATE_POOL_LOCATION_VALIDITY: "updatePoolLocationValidity",
  UPDATE_POOL_LOCATION_VALIDITY_ERROR: "updatePoolLocationValidityError",
  ADD_USER_TO_AUTO_ASSIGNMENT_POOL: "addUserToAutoAssignmentPool",
  ADD_USER_TO_AUTO_ASSIGNMENT_POOL_ERROR: "addUserToAutoAssignmentPoolError",
  DELETE_USER_FROM_AUTO_ASSIGNMENT_POOL: "deleteUserFromAutoAssignmentPool",
  DELETE_USER_FROM_AUTO_ASSIGNMENT_POOL_ERROR:
    "deleteUserFromAutoAssignmentPoolError",
  UPDATE_USER_TO_AUTO_ASSIGNMENT_POOL: "updateUserToAutoAssignmentPool",
  UPDATE_USER_TO_AUTO_ASSIGNMENT_POOL_ERROR:
    "updateUserToAutoAssignmentPoolError",
  ZOOM_MEETING_SUCCESS: "zoomMeetingSuccess",
  ZOOM_MEETING_ERROR: "zoomMeetingError",
  ZOOM_NUMBER_COPIED: "zoomNumberCopied",
  EDIT_LEAD_SUCCESS: "editLeadSuccess",
  EDIT_LEAD_ERROR: "editLeadError",
  CREATE_QUARTER_SUCCESS: "createQuarterSuccess",
  CREATE_QUARTER_ERROR: "createQuarterError",
  EDIT_QUARTER_SUCCESS: "editQuarterSuccess",
  EDIT_QUARTER_ERROR: "editQuarterError",
  EDIT_AGENT_TARGET_SUCCESS: "editAgentTargetSuccess",
  EDIT_AGENT_TARGET_ERROR: "editAgentTargetError",
  CREATE_SALE_SUCCESS: "createSaleSuccess",
  CREATE_SALE_ERROR: "createSaleError",
  UPDATE_SALE_SUCCESS: "updateSaleSuccess",
  UPDATE_SALE_ERROR: "updateSaleError",
  APPROVE_SALE_COMMISSION_SUCCESS: "approveSaleCommissionSuccess",
  APPROVE_SALE_COMMISSION_ERROR: "approveSaleCommissionError",
  DISPUTE_SALE_COMMISSION_SUCCESS: "disputeSaleCommissionSuccess",
  DISPUTE_SALE_COMMISSION_ERROR: "disputeSaleCommissionError",
  ADD_LEAD_SUCCESS: "addLeadSuccess",
  ADD_LEAD_ERROR: "addLeadError",
  ADD_CUSTOMER_SUCCESS: "addCustomerSuccess",
  ADD_CUSTOMER_ERROR: "addCustomerError",
  ADD_TRACK_SUCCESS: "addTrackSuccess",
  ADD_TRACK_ERROR: "addTrackError",
  ADD_TRACK_TYPE_SUCCESS: "addTrackTypeSuccess",
  ADD_TRACK_TYPE_ERROR: "addTrackTypeError",
  MOVE_LEAD_SUCCESS: "moveLeadSuccess",
  MOVE_LEAD_ERROR: "moveLeadError",
  MOVE_LEAD_UNDER_PROCESSING: "moveLeadUnderPorcessing",
  EDIT_USER_LOCATION_SUCCESS: "editUserlocationsettingssuccess",
  EDIT_USER_LOCATION_ERROR: "edituserlocationsettingserror",
  DELETE_USER_LOCATION_SUCCESS: "deleteUserLocationSuccess",
  DELETE_USER_LOCATION_ERROR: "deleteuserlocationerror",
  ACTIVATE_DUPLICATE_LOCATION_SUCCESS: "setduplicaterowactivesuccess",
  ACTIVATE_DUPLICATE_LOCATION_ERROR: "setduplicaterowactiveerror",
  ACTIVATE_USER_LOCATION_SUCCESS: "activateUserLocationSuccess",
  ACTIVATE_USER_LOCATION_ERROR: "activateuserlocationerror",
  EDIT_WEIGHT_SUCCESS: "editweightSuccess",
  EDIT_WEIGHT_ERROR: "editweightError",
  DELETE_POOL_SUCCESS: "deletePoolSuccess",
  UPLOAD_ICON_SUCCESS: "uploadIconSuccess",
  UPLOAD_ICON_ERROR: "uploadIconError",
  DELETE_ICON_SUCCESS: "deleteIconSuccess",
  DELETE_ICON_ERROR: "deleteIconError",
  EDIT_ICON_SUCCESS: "editIconSuccess",
  EDIT_ICON_ERROR: "editIconError",
  DELETE_POOL_ERROR: "deletePoolError",
  ADD_POOL_SUCCESS: "addPoolSuccess",
  ADD_POOL_ERROR: "addPoolError",
  UPDATE_POOL_SUCCESS: "updatePoolSuccess",
  UPDATE_POOL_ERROR: "updatePoolError",
  UNLINK_LOCATION_SUCCESS: "unlinkLocationSuccess",
  UNLINK_LOCATION_ERROR: "unlinkLocationError",
  LINK_LOCATION_SUCCESS: "linkLocationSuccess",
  LINK_LOCATION_ERROR: "linkLocationError",
  UPDATE_RULE_ERROR: "updateRuleError",
  UPDATE_RULE_SUCCESS: "updateRuleSuccess",
  DELETE_RULE_ERROR: "deleteRuleError",
  DELETE_RULE_SUCCESS: "deleteRuleSuccess",
  ADD_RULE_ERROR: "addRuleError",
  ADD_RULE_SUCCESS: "addRuleSuccess",

  ADD_STATUS_SUCCESS: "addStatusSuccess",
  ADD_STATUS_ERROR: "addStatusError",
  UPDATE_STATUS_SUCCESS: "updateStatusSuccess",
  UPDATE_STATUS_ERROR: "updateStatusError",
  ADD_STATUS_ROTATION_SUCCESS: "addStatusRotationSuccess",
  ADD_STATUS_ROTATION_ERROR: "addStatusRotationError",
  UPDATE_STATUS_ROTATION_SUCCESS: "updateStatusRotationSuccess",
  UPDATE_STATUS_ROTATION_ERROR: "updateStatusRotationError",
  DELETE_STATUS_ROTATION_SUCCESS: "deleteStatusRotationSuccess",
  DELETE_STATUS_ROTATION_ERROR: "deleteStatusRotationError",
  ADD_STATUS_FORM_SUCCESS: "addStatusFormSuccess",
  ADD_STATUS_FORM_ERROR: "addStatusFormError",
  DELETE_STATUS_FORM_SUCCESS: "deleteStatusFormSuccess",
  DELETE_STATUS_FORM_ERROR: "deleteStatusFormError",
  UPDATE_STATUS_FORM_SUCCESS: "updateStatusFormSuccess",
  UPDATE_STATUS_FORM_ERROR: "updateStatusFormError",
  LINK_STATUS_FORM_SUCCESS: "linkStatusFormSuccess",
  LINK_STATUS_FORM_ERROR: "linkStatusFormError",
  UNLINK_STATUS_FORM_SUCCESS: "unlinkStatusFormSuccess",
  UNLINK_STATUS_FORM_ERROR: "unlinkStatusFormError",
  ADD_STATUS_FORM_FIELD_SUCCESS: "addStatusFormFieldSuccess",
  ADD_STATUS_FORM_FIELD_ERROR: "addStatusFormFieldError",
  DELETE_STATUS_FORM_FIELD_SUCCESS: "deleteStatusFormFieldSuccess",
  DELETE_STATUS_FORM_FIELD_ERROR: "deleteStatusFormFieldError",
  UPDATE_STATUS_FORM_FIELD_SUCCESS: "updateStatusFormFieldSuccess",
  UPDATE_STATUS_FORM_FIELD_ERROR: "updateStatusFormFieldError",
  ADD_DEFAULT_USER_SUCCESS: "addDefaultUserSuccess",
  DELETE_DEFAULT_USER_SUCESS: "deleteDefaultUserSuccess",
  DELETE_DEFAULT_USER_ERROR: "deleteDefaultUserError",
  ADD_DEFAULT_USER_ERROR: "addDefaultUserError",
  EDIT_FUNNEL_SUCCESS: "editFunnelSuccess",
  EDIT_FUNNEL_ERROR: "editFunnelError",
  ADD_FUNNEL_SUCCESS: "addFunnelSuccess",
  ADD_FUNNEL_ERROR: "addFunnelError",
  DELETE_FUNNEL_SUCCESS: "deleteFunnelSuccess",
  DELETE_FUNNEL_ERROR: "deleteFunnelError",
  ADD_WHATSAPP_URL_SUCCESS: "addWhatsAppUrlSuccess",
  ADD_WHATSAPP_URL_ERROR: "addWhatsAppUrlError",
  UPLOAD_LEADS_SUCCESS: "uploadLeadsSuccess",
  UPLOAD_LEADS_ERROR: "uploadLeadsError",
  SEND_VERIFICATION_CODE_ERROR: "sendVerificationCodeError",
  CREATE_TARGET_SUCCESS: "createTargetSuccess",
  CREATE_TARGET_ERROR: "createTargetError",
  GET_LEADS_ERROR: "getLeadsError",
  GET_LEADS_403_ERROR: "getLeads403Error",
  GENERATE_API_KEY_SUCCESS: "generateApiKeySuccess",
  GENERATE_API_KEY_ERROR: "generateApiKeyError",
  EXPORT_SALES_SUCCESS: "exportSalesSuccess",
  EXPORT_SALES_ERROR: "exportSalesError",
  EXPORT_JOURNEYS_SUCCESS: "exportJourneysSuccess",
  EXPORT_JOURNEYS_ERROR: "exportJourneysError",
  EXPORT_CUSTOMERS_SUCCESS: "exportCustomersSuccess",
  EXPORT_CUSTOMERS_ERROR: "exportCustomersError",
  EXPORT_TRACKS_SUCCESS: "exportTracksSuccess",
  EXPORT_TRACKS_ERROR: "exportTracksError",
  UPDATE_USER_STATUS_SUCCESS: "updateUserStatusSuccess",
  UPDATE_USER_STATUS_ERROR: "updateUserStatusError",
  CREATE_ORGANIZATION_TARGET_SUCCESS: "createOrganizationTargetSuccess",
  CREATE_ORGANIZATION_TARGET_ERROR: "createOrganizationTargetError",
  CREATE_AGENT_TARGET_SUCCESS: "createAgentTargetSuccess",
  CREATE_AGENT_TARGET_ERROR: "createAgentTargetError",
  CREATE_TARGET_MULTIPLIER_SUCCESS: "createTargetMultiplierSuccess",
  CREATE_TARGET_MULTIPLIER_ERROR: "createTargetMultiplierError",
  CREATE_EXTRA_ACHIEVEMENT_SUCCESS: "createExtraAchievementSuccess",
  CREATE_EXTRA_ACHIEVEMENT_ERROR: "createExtraAchievementError",
  CHANGE_SUPERVISOR_SUCCESS: "changeSupervisorSuccess",
  CHANGE_SUPERVISOR_ERROR: "changeSupervisorError",
  REMOVE_MEMBER_SUCCESS: "removeMemberSuccess",
  REMOVE_MEMBER_ERROR: "removeMemberError",
  EDIT_USER_POOLS_SUCCESS: "editUserPoolsSuccess",
  EDIT_USER_POOLS_ERROR: "editUserPoolsError",
  BULK_UPDATE_STATUS_VALIDITY_SUCCESS: "bulkUpdateStatusValiditySuccess",
  BULK_UPDATE_STATUS_VALIDITY_ERROR: "bulkUpdateStatusValidityError",
  MARK_AS_INVALID_CLIENT_SUCCESS: "markAsInvalidClientSuccess",
  MARK_AS_INVALID_CLIENT_ERROR: "markAsInvalidClientError",
  CREATE_LEAD_TAG_SUCCESS: "createLeadTagSuccess",
  CREATE_LEAD_TAG_ERROR: "createLeadTagError",
  DELETE_LEAD_TAG_SUCCESS: "deleteLeadTagSuccess",
  DELETE_LEAD_TAG_ERROR: "deleteLeadTagError",
  EDIT_LEAD_TAG_SUCCESS: "editLeadTagSuccess",
  EDIT_LEAD_TAG_ERROR: "editLeadTagError",
  UPDATE_REFERRAL_VALIDITY_SUCCESS: "updateReferralValiditySuccess",
  UPDATE_REFERRAL_VALIDITY_ERROR: "updateReferralValidityError",
  REASSIGN_JOURNEY_TO_MANAGER_SUCCESS: "reassignJourneyToManagerSuccess",
  REASSIGN_JOURNEY_TO_MANAGER_ERROR: "reassignJourneyToManagerError",
  ADD_REFERRAL_SUCCESS: "addReferralSuccess",
  ADD_REFERRAL_ERROR: "addReferralError",
  ASSIGNMENT_REPORT_ERROR: "assignmentReportError",
  BLOCK_LOCATIONS_SUCCESS: "blockLocationsSuccess",
  BLOCK_LOCATIONS_ERROR: "blockLocationsError",
  UNBLOCK_LOCATIONS_SUCCESS: "unblockLocationsSuccess",
  UNBLOCK_LOCATIONS_ERROR: "unblockLocationsError",
  BLOCK_FRESH_LEADS_SUCCESS: "blockFreshLeadsSuccess",
  BLOCK_FRESH_LEADS_ERROR: "blockFreshLeadsError",
  BLOCK_LEADS_SUCCESS: "blockAllLeadsSuccess",
  BLOCK_LEADS_ERROR: "blockAllLeadsError",
  DUPLICATE_DELEGATION_SUCCESS: "duplicateDelegationSuccess",
  DUPLICATE_DELEGATION_ERROR: "duplicateDelegationError",
  ADD_AMBASSADOR_SUCCESS: "addAmbassadorSuccess",
  ADD_AMBASSADOR_ERROR: "addAmbassadorError",
  ADD_STAGE_SUCCESS: "addStageSuccess",
  ADD_STAGE_ERROR: "addStageError",
  UPDATE_STAGE_ORDER_SUCCESS: "updateStageOrderSuccess",
  UPDATE_STAGE_ORDER_ERROR: "updateStageOrderError",
  UPDATE_STAGE_SUCCESS: "updateStageSuccess",
  UPDATE_STAGE_ERROR: "updateStageError",

  ADD_TRACK_STATUS_SUCCESS: "addTrackStatusSuccess",
  ADD_TRACK_STATUS_ERROR: "addTrackStatusError",
  ADD_TRACK_TYPE_FORM_SUCCESS: "addTrackTypeFormSuccess",
  ADD_TRACK_TYPE_FORM_ERROR: "addTrackTypeFormError",
  UPDATE_TRACK_TYPE_FORM_SUCCESS: "updateTrackTypeFormSuccess",
  UPDATE_TRACK_TYPE_FORM_ERROR: "updateTrackTypeFormError",
  ADD_TRACK_TYPE_STATUS_FORM_FIELD_SUCCESS:
    "addTrackTypeStatusFormFieldSuccess",
  ADD_TRACK_TYPE_STATUS_FORM_FIELD_ERROR: "addTrackTypeStatusFormFieldError",
  UPDATE_TRACK_TYPE_STATUS_FORM_FIELD_SUCCESS:
    "updateTrackTypeStatusFormFieldSuccess",
  UPDATE_TRACK_TYPE_STATUS_FORM_FIELD_ERROR:
    "updateTrackTypeStatusFormFieldError",
  DELETE_TRACK_TYPE_STATUS_FORM_FIELD_SUCCESS:
    "deleteTrackTypeStatusFormFieldSuccess",
  DELETE_TRACK_TYPE_STATUS_FORM_FIELD_ERROR:
    "deleteTrackTypeStatusFormFieldError",
  ADD_TRACK_STATUS_FORM_SUCCESS: "addTrackStatusFormSuccess",
  ADD_TRACK_STATUS_FORM_ERROR: "addTrackStatusFormError",
  UPDATE_TRACK_STATUS_SUCCESS: "updateTrackStatusSuccess",
  UPDATE_TRACK_STATUS_ERROR: "updateTrackStatusError",
  EDIT_PROPERTY_SUCCESS: "editPropertySuccess",
  EDIT_PROPERTY_ERROR: "editPropertyError",
  UPDATE_NEXT_ACTION_SUCCESS: "updateNextActionSuccess",
  UPDATE_NEXT_ACTION_ERROR: "updateNextActionError",

  UPLOAD_DOCUMENT_SUCCESS: "uploadDocumentSuccess",
  UPLOAD_DOCUMENT_ERROR: "uploadDocumentError",
  DELETE_DOCUMENT_SUCCESS: "deleteDocumentSuccess",
  DELETE_DOCUMENT_ERROR: "deleteDocumentError",

  UPDATE_CUSTOMER_ID_INFO_SUCCESS: "updateCustomerIdInfoSuccess",
  UPDATE_CUSTOMER_ID_INFO_ERROR: "updateCustomerIdInfoError",

  UPLOAD_OWNER_ID_SUCCESS: "uploadOwnerIdSuccess",
  UPLOAD_OWNER_ID_ERROR: "uploadOwnerIdError",
  DELETE_OWNER_ID_SUCCESS: "deleteOwnerIdSuccess",
  DELETE_OWNER_ID_ERROR: "deleteOwnerIdError",
  DOWNLOAD_OWNER_ID_ERROR: "downloadOwnerIdError",

  CREATE_DRAWING_REQUEST_SUCCESS: "createDrawingRequestSuccess",
  CREATE_DRAWING_REQUEST_ERROR: "createDrawingRequestError",
  REVERT_DRAWING_REQUEST_SUCCESS: "revertDrawingRequestSuccess",
  REVERT_DRAWING_REQUEST_ERROR: "revertDrawingRequestError",

  DOWNLOAD_DOCUMENT_SUCCESS: "downloadDocumentSuccess",
  DOWNLOAD_DOCUMENT_ERROR: "downloadDocumentError",

  MARK_DOCUMENT_AS_CHOSEN_BY_CLIENT_SUCCESS:
    "markDocumentAsChosenByClientSuccess",
  MARK_DOCUMENT_AS_CHOSEN_BY_CLIENT_ERROR: "markDocumentAsChosenByClientError",

  // Meetings and Tasks Toast IDs
  MEETING_COMPLETED_SUCCESS: "meetingCompletedSuccess",
  MEETING_COMPLETED_ERROR: "meetingCompletedError",
  TASK_COMPLETED_SUCCESS: "taskCompletedSuccess",
  TASK_COMPLETED_ERROR: "taskCompletedError",
  COMPLETED_MEETINGS_LOAD_ERROR: "completedMeetingsLoadError",
  COMPLETED_TASKS_LOAD_ERROR: "completedTasksLoadError",
  ADD_SUB_PROPERTY_SUCCESS: "addSubPropertySuccess",
  ADD_SUB_PROPERTY_ERROR: "addSubPropertyError",
  EDIT_FINANCIAL_INFO_SUCCESS: "editFinancialInfoSuccess",
  EDIT_FINANCIAL_INFO_ERROR: "editFinancialInfoError",
  EDIT_HOSPITALITY_INFO_SUCCESS: "editHospitalityInfoSuccess",
  EDIT_HOSPITALITY_INFO_ERROR: "editHospitalityInfoError",
  EDIT_RENTAL_DETAILS_SUCCESS: "editRentalDetailsSuccess",
  EDIT_RENTAL_DETAILS_ERROR: "editRentalDetailsError",
  EDIT_SCOPE_SUCCESS: "editScopeSuccess",
  EDIT_SCOPE_ERROR: "editScopeError",
  ADD_INTEREST_ACTION_SUCCESS: "addInterestActionSuccess",
  ADD_INTEREST_ACTION_ERROR: "addInterestActionError",

  ADD_TASK_SUCCESS: "addTaskSuccess",
  ADD_TASK_ERROR: "addTaskError",
  TASK_EVALUATION_ERROR: "taskEvaluationError",

  ADD_LISTING_SUCCESS: "addListingSuccess",
  ADD_LISTING_ERROR: "addListingError",
  EDIT_LISTING_SUCCESS: "editListingSuccess",
  EDIT_LISTING_ERROR: "editListingError",
  PUBLISH_LISTING_SUCCESS: "publishListingSuccess",
  PUBLISH_LISTING_ERROR: "publishListingError",
  UNPUBLISH_LISTING_SUCCESS: "unpublishListingSuccess",
  UNPUBLISH_LISTING_ERROR: "unpublishListingError",
  MARK_LISTING_AS_SOLD_SUCCESS: "markListingAsSoldSuccess",
  MARK_LISTING_AS_SOLD_ERROR: "markListingAsSoldError",
  MARK_LISTING_AS_AVAILABLE_SUCCESS: "markListingAsAvailableSuccess",
  MARK_LISTING_AS_AVAILABLE_ERROR: "markListingAsAvailableError",
  ADD_INTEREST_SUCCESS: "addInterestSuccess",
  ADD_INTEREST_ERROR: "addInterestError",
  ADD_INTEREST_TYPE_SUCCESS: "addInterestTypeSuccess",
  ADD_INTEREST_TYPE_ERROR: "addInterestTypeError",
  ADD_INTEREST_ACTION_TYPE_SUCCESS: "addInterestActionTypeSuccess",
  ADD_INTEREST_ACTION_TYPE_ERROR: "addInterestActionTypeError",
  EDIT_INTEREST_ACTION_TYPE_SUCCESS: "editInterestActionTypeSuccess",
  EDIT_INTEREST_ACTION_TYPE_ERROR: "editInterestActionTypeError",
  DISABLE_INTEREST_ACTION_TYPE_SUCCESS: "disableInterestActionTypeSuccess",
  DISABLE_INTEREST_ACTION_TYPE_ERROR: "disableInterestActionTypeError",
  ENABLE_INTEREST_ACTION_TYPE_SUCCESS: "enableInterestActionTypeSuccess",
  ENABLE_INTEREST_ACTION_TYPE_ERROR: "enableInterestActionTypeError",
  RESOLVE_RATING_SUCCESS: "resolveRatingSuccess",
  RESOLVE_RATING_ERROR: "resolveRatingError",
  ADD_FINANCIAL_PROPOSAL_SUCCESS: "addFinancialProposalSuccess",
  ADD_FINANCIAL_PROPOSAL_ERROR: "addFinancialProposalError",
  EDIT_FINANCIAL_PROPOSAL_SUCCESS: "editFinancialProposalSuccess",
  EDIT_FINANCIAL_PROPOSAL_ERROR: "editFinancialProposalError",
  LOAD_FINANCIAL_PROPOSAL_ERROR: "loadFinancialProposalError",
  ADD_STATUS_IDENTIFIER_SUCCESS: "addStatusIdentifierSuccess",
  ADD_STATUS_IDENTIFIER_ERROR: "addStatusIdentifierError",
  ARCHIVE_TRACK_SUCCESS: "archiveTrackSuccess",
  ARCHIVE_TRACK_ERROR: "archiveTrackError",
  UNARCHIVE_TRACK_SUCCESS: "unarchiveTrackSuccess",
  UNARCHIVE_TRACK_ERROR: "unarchiveTrackError",
  GENERIC_SUCCESS: "genericSuccess",
  GENERIC_ERROR: "genericError",
  EXTEND_FINANCIAL_PROPOSAL_ERROR: "extendFinancialProposalError",
  EXTEND_FINANCIAL_PROPOSAL_SUCCESS: "extendFinancialProposalSuccess",
};
export type ZoomLevel = any;
export interface FilterMethodDateType {
  filterKeyTo?: string;
  filterKeyFrom?: string;
  filterKey?: string;
  startDateValue: string | null;
  endDateValue: string | null;
  filterValue?: string | number;
}

export interface DefaultFilterMethodType {
  filterKey: string;
  filterValue: string | number;
}

export type CommonTableColumns = {
  filter?:
    | "select"
    | "text"
    | "number"
    | "date"
    | "multiSelect"
    | "autoComplete"
    | "textRange";
  options?: { value: string | number; label: string }[];
  id?: string;
  accessor: any;
  onFilter?: any;
  onFilterFrom?: any;
  onFilterTo?: any;
  onClearFilter?: () => void;
  filterKey?: string;
  filterKeyFrom?: string;
  filterKeyTo?: string;
  Header: string;
  Cell?: ({ value }: any) => JSX.Element;
  minWidth: number;
  fetchOptions?: () => Promise<{ value: any; label: any }[]>;
  loading?: boolean;
  setSearchTerm?: (value: string) => void;
  searchTerm?: string;
  hideColumn?: boolean;
};
export type GetRowBackgroundColor = (
  row: any,
  rowKeyValue?: { key: string; value: any },
) => string | null;

export type CommonColumns = CommonColumns[];

export interface FileUploadProps {
  setFiles: (files: File[]) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number;
  maxFiles?: number;
  onDeleteFile: (file: UploadedFile) => void;
  label?: string;
}

export interface UploadedFile {
  id: string;
  uploadStatus: "uploading" | "success" | "error";
  name: string;
  type: string;
  size: number;
  lastModified: number;
  errorMessage?: string;
  preview: string | null;
}

export type Nullable<T> = { [K in keyof T]: T[K] | null };

export enum LocationDataParams {
  AREA = "Area",
  DEVELOPER = "Developer",
  COMPOUND = "Compound",
}

export enum PermissionsEnum {
  ALL = "*",
  NAWY_NOW_PARTNERS = "nawy-now-partners",
  /** §KanbanGate: may this user switch the leads page to the Nawy Now applications board at all. */
  NAWY_NOW_KANBAN = "nawy-now-kanban",
  /** §Terminate: must equal the UMS resource name workflow-manager checks server-side. */
  NAWY_NOW_TERMINATE_FLOW = "can-terminate-nawy-now-flow",
  /** §Multi-application: may start another application on a lead that already has an ACTIVE one — must equal
   * the UMS resource name workflow-manager checks server-side. */
  NAWY_NOW_ADD_MULTIPLE_APPS = "nawy-now-can-add-multiple-apps",
  NAWY_NOW_CALCULATOR = "nawy_now_calculator",
  NAWY_UNLOCKED_CALCULATOR = "nawy_unlocked_calculator",
  AGENTS = "agents",
  CUSTOMERS = "customers",
  DASHBOARDS = "dashboards",
  SALES_LEAGUE = "sales_league",
  AGENTS_COMMISSIONS = "agents_commissions",
  UPLOAD_LIST_LEADS = "upload_list_leads",
  DAILY_OPERATIONS = "daily_operations",
  SEARCH_COMMENTS = "search_comments",
  AGENTS_TREE = "agents_tree",
  SETTINGS_TAB = "settings_tab",
  COMMON_SETTINGS = "common_settings",
  FACEBOOK_RULES = "facebook_rules",
  USERS_PAGE = "users_page",
  NAWY_NOW_CALCULATOR_COMMISSION_SECTION = "nawy_now_calculator_commission_section",
  JOURNEY_SALES = "journey_sales",
  JOURNEY_COLLECTION = "journey_collection",
  JOURNEY_STATUS_NEGATIVE_UPDATES_COMMENTS = "journey_status_negative_updates_comments",
  EDIT_JOURNEY_CAMPAIGN_DETAILS = "edit_journey_campaign_details",
  JOURNEY_ASSIGNED_USER = "journey_assigned_user",
  ADD_WHATSAPP_URL = "add_whatsapp_url",
  JOURNEY_INFO = "journey_info",
  JOURNEY_HISTORY = "journey_history",
  DELETE_CUSTOMER_TELEPHONE = "delete_customer_telephone",
  TOGGLE_DONOT_CALL_CUSTOMER = "toggle_donot_call_customer",
  MERGE_JOURNEY = "merge_journey",
  RESOLVE_USERNAME_CONFLICT = "resolve_username_conflict",
  AUTO_ASSIGN_JOURNEY = "auto_assign_journey",
  GET_ZOOM_LINK = "get_zoom_link",
  ASSIGNED_AGENT = "assigned_agent", // current agent assigned to journey
  MARK_AS_RUBBISH = "mark_as_rubbish",
  SKIP_JOURNEY_HIERARCHY = "skip_journey_hierarchy",
  EXPORT_SALES = "export-sales",
  TRANSFER_TO_TASKEEN = "transfer_to_taskeen",
  TRANSFER_TO_KEY = "transfer_to_key",
  ARCHIVE_JOURNEY = "archive_journey",
  DELETE_JOURNEY = "delete_journey",
  MANAGE_JOURNEY_VIEWERS = "manage_journey_viewers",
  LINK_LOCATION = "link_location",
  NOTIFY_ASSIGNED_AGENT = "notify_assigned_agent",
  SELECT_JOURNEYS = "select_journeys",
  SHOW_ARCHIVED_JOURNEYS = "show_archived_journeys",
  SHOW_UNASSIGNED_JOURNEYS = "show_unassigned_journeys",
  SHOW_UNCONTACTED_JOURNEYS = "show_uncontacted_journeys",
  GET_ONLY_ARCHIVED_JOURNEYS = "get_only_archived_journeys",
  REVERSE_ASSIGNED_USER_FILTER = "reverse_assigned_user_filter",
  ASSIGNED_USERS_FILTER = "assigned_users_filter",
  JOURNEY_SOURCE_FILTER = "journey_source_filter",
  LOCATION_FILTER = "location_filter",
  SUPERVISORS_FILTER = "supervisors_filter",
  ADD_BULK_CUSTOMERS = "add_bulk_customers",
  EXPORT_JOURNEYS = "export_journeys",
  MOVE_JOURNEYS = "move_journeys",
  BULK_MOVE_JOURNEYS_MAX = "bulk_move_journeys_max",
  JOURNEYS_TABLE_ACTIONS_DROPDOWN = "journeys_table_actions_dropdown",
  ADD_NEW_CUSTOMER_AGENT_MOBILE = "add_new_customer_agent_mobile",
  TARGETS_FILTER = "targets_filter",
  HOME_TARGETS = "home_targets",
  NAWY_NOW_PROFITABILITY = "nawy_now_profitability",
  EDIT_PERSONAL_TARGET = "edit_personal_target",
  LEAD_TAGS = "lead_tags",
  TARGET_ADJUSTMENTS = "target_adjustments",
  EXTRA_ACHIEVEMENTS = "extra_achievements",
  JOURNEY_LOCATION = "journey_location",
  JOURNEY_DATE_OF_LAST_REQUEST = "journey_date_of_last_request",
  JOURNEY_DATE_OF_LAST_CONTACT = "journey_date_of_last_contact",
  JOURNEY_METHOD_OF_CONTACT = "journey_method_of_contact",
  JOURNEY_BEST_TIME_TO_CALL = "journey_best_time_to_call",
  CUSTOMER_EMAIL = "customer_email",
  NEXT_ACTION = "next_action",
  MORE_ACTIONS_JOURNEYS_TABLE = "more_actions_journeys_table",
  ADD_DUPLICATE_JOURNEY = "add_duplicate_journey",
  TEAM_LISTINGS = "team_listings",
  UPDATE_LISTING_PRICE = "update_listing_price",
  MOVE_LISTING = "move_listing",
  DELETE_COMMENT = "delete_comment",
  CAN_ASSIGN_FRESH_USERS_JOURNEY = "can_assign_fresh_users",
  CAN_ASSIGN_NON_AVAILABLE_USERS_JOURNEY = "can_assign_non_available_users_journey",
  STATUS_FORM_FIELDS = "status_form_fields",
  TARGETS_CONFIGURATIONS_PAGE = "targets_configurations_page",
  TARGETS_CONFIGURATIONS_AGENT_TARGETS = "targets_configurations_agent_targets",
  TARGETS_CONFIGURATIONS_ORGANIZATION_TARGETS = "targets_configurations_organization_targets",
  TARGETS_CONFIGURATIONS_TARGET_MULTIPLIERS = "targets_configurations_target_multipliers",
  SHOW_PARKED_LEADS = "show_parked_leads",
  SHOW_RUBBISH_LEADS = "show_rubbish_leads",
  SHOW_DATABASE_LEADS = "show_database_leads",
  CAN_CREATE_SALE = "can_create_sale",
  UPDATE_STATUS_FROM_HOME = "update_status_from_home",
  REASSIGN_FROM_HOMEPAGE = "reassign_from_homepage",
  BULK_MARK_AS_RUBBISH = "bulk_mark_as_rubbish",
  CROSS_ACCOUNTMANAGERS_SELECTION = "cross_accountmanagers_selection",
  PAGINATION_100 = "pagination_100",
  PAGINATION_200 = "pagination_200",
  PAGINATION_300 = "pagination_300",
  PAGINATION_400 = "pagination_400",
  PAGINATION_500 = "pagination_500",
  BLOCK_FRESH_LEADS_AND_LOCATIONS = "block_fresh_leads_and_locations",
  ALLOW_AUTO_DELEGATION = "allow_auto_delegation",
  SHOW_LEAD_SOURCE = "show_lead_source",
  MANAGE_REFERRALS = "manage_referrals",
  ADD_ALL_LEAD_TYPES = "add_all_lead_types",
  JOURNEY_ASSIGNMENT_REPORT = "journey_assignment_report",
  VIEW_PERSONAL_ACHIEVEMENT = "view_personal_achievement",
  PREVIOUS_STATUS_FILTER = "previous_status_filter",
  PREVIOUS_ASSIGNED_USER_FILTER = "previous_assigned_user_filter",
  EXCLUDE_LEADS_FILTERS = "exclude_leads_filters",
  DAILY_OPERATIONS_INTERNAL_TESTING = "daily_operations-internal-testing",
  SAVED_FILTERS = "saved_filters",
  SELECT_CLIENTS = "select_clients",
  EXPORT_CUSTOMERS = "export_customers",
  MORE_ACTIONS_CUSTOMERS_TABLE = "more_actions_customers_table",
  TRACKS = "tracks",
  EXPORT_TRACKS = "track-exports",
  VIEW_ALL_TRACKS = "view-all-tracks",
  SEND_MARKET_UPDATES = "announce-market-updates-notification",
  ADMIN_VIEW_AMBASSADORS = "admin-view-ambassadors",
  CAN_VIEW_IN_CALL_TAG = "can_view_in_call_tag",
  DISABLE_AI_EVALUATION = "disable_ai_evaluation",
  CAN_ACCESS_WRITE_RATINGS = "can_access_write_ratings",
  CAN_ACCESS_READ_RATINGS = "can_access_read_ratings",
  CAN_VIEW_RATINGS_TAB = "can_view_ratings_tab",
  PREVENT_EDIT_STATUS = "prevent_edit_status",
  SHOW_UNIT_ID_LISTING_DASHBOARD = "show_unit_id_listing_dashboard",
  HIDE_PROPERTY_TRACKS = "hide-property-track",
  CONTACT_CENTER = "contact_center_agents",
  BULK_REASSIGN_TRACKS = "bulk-reassign-tracks",
  HIDE_CAMPAIGN_COLUMNS = "hide-campaign-columns",
  ARCHIVE_TRACK = "archive-track",
  HIDE_LEAD_TYPE_ACTIONS = "hide_lead_type_actions",
  SKIP_STICKY_FILTERS = "skip_sticky_filters",
  TASKS_MANAGER_ADMIN_VIEW = "tasks_manager_admin_view",
  TRACK_TYPE_FORM_FIELDS = "track_type_form_fields",
  ADD_TRACK_COMMENT = "add-track-comment",
  ADD_CONSTRUCTION_UPDATE = "add-construction-update",
  JOURNEY_COPY_ACCESS_LINK = "journey_copy_access_link",
  DEVELOPER_TRACKS_TAB = "developer_tracks_tab",
  UNPUBLISH_LISTING = "unpublish-listing",
  RENTAL_INVENTORY = "rental-inventory",
  DEVELOPER_TRACKS = "developer-tracks",
  PRIMARY_DEVELOPER_USERS = "primary-developer-users",
  MANAGE_RESTRICTED_LEAD_TAGS = "manage_restricted_lead_tags",
  EXPO_DASHBOARD = "expo_dashboard",
  QR_CODE_EXPO_BULK = "qr_code_expo_bulk",
  SETTINGS_PAGE = "settings_page",
  ZOOM_CALL_ANALYSIS = "zoom_call_analysis",
  ZOOM_CALL_TRANSCRIPTION = "zoom_call_transcription",
  ZOOM_CALL_RECORDING = "zoom_call_recording",
  ZOOM_CALLS_ADMIN = "zoom_calls_admin",
  ZOOM_CALL_AI_STATUS = "zoom_call_ai_status",
  PROPERTY_TRACKS_HOME = "property_tracks_home",
  UPDATE_FEATURE_FLAG = "update-feature-flag",
  ADMIN_EDIT_PROPERTY = "admin-edit-property",
  NAWY_NOW_TAB = "nawy_now_tab",
}

export interface RanksResponse {
  data: {
    page: number;
    pageSize: number;
    total: number;
    results: Rank[];
  };
}

export enum UserStatus {
  Blocked = "blocked",
  Fresh = "fresh",
  Active = "active",
  Banned = "banned",
}

export interface UserConfiguration {
  id: number;
  oldId: number | null;
  migrationOrgId: number;
  uid: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userId: number;
  reassignmentEligibility: boolean;
  delegationWeights: string;
  duplicatesDelegationFlag: boolean;
  duplicateStopperFlag: boolean;
  isAvailable: boolean;
  userStatus: UserStatus;
  stopDelegationFlag: boolean;
  autoAssignmentWeight: string;
  lastActivityDate: string;
  rotationManager: boolean;
  user: User;
}

export interface UserConfigurationsResponse {
  data: UserConfiguration;
}

export interface SubDepartmentHead {
  id: number;
  uid: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null | string;
  name: string;
}

interface DepartmentHead {
  id: number;
  uid: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null | string;
  name: string;
}

interface Department {
  name: string;
  id: number;
  uid: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null | string;
  head: DepartmentHead;
}

export interface SubDepartment {
  name: string;
  id: number;
  uid: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null | string;
  head: SubDepartmentHead;
  department: Department;
  hasTarget: boolean;
}

export interface SubDepartmentsDto extends BaseResponse {
  data: BasePaginationResponse<SubDepartment>;
}

export interface Configuration {
  uid: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userId: number;
  reassignmentEligibility: boolean;
  delegationWeights: number;
  duplicatesDelegationFlag: boolean;
  isAvailable: boolean;
  autoAssignmentWeight: string;
  lastActivityDate: string;
  stopDelegationFlag: boolean;
  blockFreshLeads: boolean;
  blockAllLeads: boolean;
  rotationManager: boolean;
}

export interface Subordinate {
  uid: string;
  directSubordinates: number;
  totalSubordinates: number;
  name: string;
  color: string;
  configuration: Configuration;
}

export interface BlockedLocation {
  uid: string;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  name: string;
  locationType: string;
  compoundId: string | null;
}

export interface DirectSubordinateResults {
  uid: string;
  status: UserStatus;
  directSubordinates: number;
  totalSubordinates: number;
  color: string;
  name: string;
  teamSize: number;
  subordinates?: Subordinate[];
  configuration: Configuration;
  rank: Rank;
  blockedLocations: BlockedLocation[];
}

export interface DirectSubordinateResponse extends BasePaginationResponse<DirectSubordinateResults> {}

export enum OrganizationTierTypes {
  NAWY = "nawy",
  TASKEEN = "taskeen",
  PARTNERS = "partner",
  FRANCHISE = "franchise",
}

export enum SwitchColors {
  primary = "primary",
  secondary = "secondary",
  error = "error",
  info = "info",
  success = "success",
  warning = "warning",
  default = "default",
}
export interface OrganizationViewWrapperProps {
  children: ReactNode;
  isMultiple?: boolean;
  showMultipleOrganizationTables?: boolean;
  onApply?: () => void;
  onReset?: () => void;
}

export enum PermissionLevel {
  READ = "read",
  WRITE = "write",
  ALL = "all",
}

export type Permission = {
  name: PermissionsEnum;
  level: PermissionLevel;
};

export enum WhatsappInstanceStatus {
  Created = "created",
  Open = "open",
  Connecting = "connecting",
  Close = "close",
  Refused = "refused",
  Pairing = "pairing",
  Logout = "logout",
}

export interface WhatsappInstance {
  status: WhatsappInstanceStatus | "not-required" | "not-created";
  required: boolean;
  gracePeriodStartedAt: string | null;
  gracePeriodEndedAt: string | null;
  lastConnectedAt: string | null;
  lastDisconnectedAt: string | null;
  instanceId: string | null;
}

export interface BlockedLocationsProps {
  userId: UUID;
  blockedLocations: BlockedLocation[];
}
export enum SavedFilterScreen {
  LEADS = "leads",
  SALES = "sales",
  NEXT_ACTIONS = "next_actions",
  LISTING_DASHBOARD = "listing_dashboard",
}

export type SavedFiltersMap = {
  leads: JourneysFilters;
  sales: SalesFilters;
  next_actions: NextActionsFilters;
  listing_dashboard: ListingDashboardFilters;
  clients: ClientSearchFilters;
};

export interface BaseLookupsFilters {
  statusNames?: string[];
  minDateOfLastRequest?: string;
  maxDateOfLastRequest?: string;
  maxDateOfLastContact?: string;
  minDateOfLastContact?: string;
  userUIds?: string[];
  search?: string;
  campaignNames?: string[];
  methodOfContactNames?: string[];
  adsetNames?: string[];
  leadTypeNames?: string[];
  locationUIds?: string[];
  leadSourceNames?: string[];
  supervisorUIds?: string[];
  statusCategoriesNames?: string[];
  launchUIds?: string[];
  showArchivedJourneys?: boolean;
  showListLeads?: boolean;
  reassigned?: boolean;
  hadMeeting?: boolean;
  showViewOnly?: boolean;
  showOnlyArchived?: boolean;
  showUnassignedLeads?: boolean;
  showUncontacted?: boolean;
  parkedOnly?: boolean;
  rubbishOnly?: boolean;
  databaseOnly?: boolean;
  hasListings?: boolean;
  showOnlyFreshLeads?: boolean;
  excludeOldLeads?: boolean;
  excludeQrCodeSent?: boolean;
  leadState?: string;
}

export interface FilterOptionsParams {
  initialParams?: Partial<LookupsParams>;
  enabled?: boolean;
}

export interface Options {
  value: string | number | UUID | undefined;
  optionValueUid?: string | null;
  label: string | undefined;
  color?: string;
  oldColor?: string;
  new_color?: string;
  developerName?: string;
  phoneNumber?: string;
}

export interface DynamicFormProps {
  handleConfirm?: (values: any, formType?: "sale" | "form") => void;
  formFields: FormFieldsResponse;
  isLoadingSubmition?: boolean;
  disabledConfirmButton?: boolean;
  handleClose?: () => void;
  hideFooter?: boolean;
  onChange?: (values: any) => void;
}

export interface SettingsDrawerProps {
  children: React.ReactNode;
  open: boolean;
  setOpen?: (open: boolean) => void;
  isLoading?: boolean;
  handleCloseModal: () => void;
  handleClearFilters: () => void;
  handleSearch: () => void;
}

export interface AppliedFilters {
  label: string;
  value: string | string[];
}

export interface RelationDto {
  id: number;
  name: string;
}
export interface ForeignRelationDto {
  uid: string;
  name: string;
}

export interface FilterDto {
  id: number;
  name: string;
  color: string;
}

export type SettingsListProps = {
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  handleItemClick: (path: string) => void;
  handleOpen: (event: React.MouseEvent<HTMLElement>) => void;
};

export type MoreListProps = {
  handleItemClick: (path: string) => void;
  showUploadLeadsModal: () => void;
  StyledListItemButton: React.ComponentType<ListItemButtonProps>;
  SubMenuListItemButton: React.ComponentType<ListItemButtonProps>;
  getNavLinkStyles: (path: string) => boolean;
  erealtyURL?: string;
};

export interface MainSidebarListProps {
  getNavLinkStyles: (path: string) => boolean;
  handleItemClick: (path: string) => void;
  showUploadLeadsModal?: () => void;
  StyledListItemButton: React.ComponentType<ListItemButtonProps>;
  SubMenuListItemButton: React.ComponentType<ListItemButtonProps>;
  handleCalendarClick?: () => void;
  handleClipboardClick?: () => void;
}

// Task Classification Enum
export type TaskAiClassification =
  | "Actionable"
  | "Can Be Better"
  | "Not Actionable";

// Task AI Evaluation DTOs
export interface TaskAiEvaluationResponse {
  classification: TaskAiClassification;
  reason: string;
}

export interface AppDocument {
  id: number;
  uid: string;
  fileUrl: string;
  fileName?: string;
}

export interface BasePaginationRequest {
  page: number;
  pageSize: number;
  orderBy?: string;
  orderDirection?: string;
}

export interface ExcludeToggleProps {
  /** Filter key holding the boolean flag, e.g. "excludeStatuses" */
  filterKey: string;
  /** Filter key holding the selection this flag inverts, e.g. "statusNames" */
  dependsOnKey: string;
  filtersMemo: any;
  setFiltersMemo: (filters: any) => void;
  label?: string;
  tooltipMessage?: string;
}
